import React from "react";
import { AnimatePresence, motion } from "motion/react";
import "./App.css";
import "./styles/textures.css";
import ThemeSwitcher from "./components/ThemeSwitcher";
import ThemeTransition from "./components/ThemeTransition";
import FilmGrain from "./textures/FilmGrain";
import { TEXTURES } from "./textures/specs";
import { useTheme } from "./themes/ThemeProvider";
import type { CardItem } from "./themes/types";

const NAME = "Wayne Wang";
const TAGLINE = "Building intelligent systems";

// Data
type Experience = {
  title: string;
  link?: string;
  role: string;
  dates: string;
  description: string;
};

const experiences: Experience[] = [
  {
    title: "Arca Wealth",
    link: "https://www.arcawealth.com/",
    role: "Founding Member of Technical Staff",
    dates: "Jul 2025 - Present",
    description: "Eng #2, AI/ML.",
  },
  {
    title: "Thoughtly",
    role: "Lead AI/ML Engineer",
    dates: "May 2024 - Jun 2025",
    description: "Built AI/ML infrastructure from the ground up as first engineering hire. Enhanced GenAI intelligence with RAG, few-shot prompting, and custom evaluation pipelines. Scaled system to handle production voice AI workloads.",
  },
  {
    title: "Berkeley EECS",
    role: "Researcher",
    dates: "Jan 2023 - Jan 2024",
    description: "Open-source hardware RL simulation system CktGym with Professor Vladimir Stojanovic. Built end-to-end infrastructure using OpenAI Gym and Ray on GCP. Funded by NSF and Intel.",
  },
  {
    title: "GotHub.AI",
    role: "Founding Software Engineer",
    dates: "Jun 2023 - Mar 2024",
    description: "Built data engine from scratch in 2 months. Developed MultiAgent Framework with OpenAI and Llama2 using Django. Won $300K funding from MiraclePlus. Deployed on AWS with custom Kubernetes setup.",
  },
  {
    title: "ColossalAI",
    role: "MLE Intern",
    dates: "Jan 2022 - Aug 2022",
    description: "GAN-based work with PCA techniques for distributed training optimization.",
  },
  {
    title: "BART",
    role: "SWE Intern",
    dates: "Jan 2021 - May 2021",
    description: "Built full-stack heat map application in Splunk for resource optimization and capacity planning.",
  },
  {
    title: "Deloitte",
    role: "Consultant Intern (Tax)",
    dates: "Jun 2022 - Aug 2022",
    description: "Quantitative analysis on financial data for Silicon Valley real estate tax preparation.",
  },
  {
    title: "UC Berkeley Statistics",
    role: "Undergraduate Student Instructor",
    dates: "May 2022 - Aug 2022",
    description: "Course instructor for Stat20, Introduction to Probability and Statistics.",
  },
  {
    title: "UC Berkeley HaaS Business School",
    role: "Research Assistant",
    dates: "Aug 2021 - Dec 2021",
    description: "Statistical analysis on economics publication data using regression models and feature engineering.",
  },
];

const projects = [
  {
    name: "Perceptus Labs",
    description: "Including LLM / VLM, real time orchestraion, and autonomous DevOps. PerceptusLabs is unified software infra for robots to see, hear, and act on the world. Enabling robots development to be as agile as the modern AI world.",
    githubUrl: "https://github.com/Perceptus-Labs",
  },
  {
    name: "Generative-CNN for Candlestick Images",
    description: "DC-GAN and CNN Inception V3 for financial pattern recognition in candlestick charts. Synthesize training data to massively reduce dataset requirements for vision models in quantitative finance. First author paper accepted to ICAIF 2024, ranked top 10 of all submissions and contender for Most Innovative Paper Award.",
    githubUrl: "https://github.com/wayne-wang-1119/Generative-CNN-for-Candlestick-Images",
  },
  {
    name: "GotHub",
    description: "AI agent that writes code and generates dev containers, returning GitHub pull requests automatically. Built with multi-agent LLM framework using OpenAI and Llama2, implemented in Django with focus on scalability. Deployed on AWS EC2 with custom Kubernetes setup for scaling services. Won $300K term sheet from MiraclePlus and $10K first place at MoleDAO 2023 AI Hackathon.",
    githubUrl: "https://github.com/wayne-wang-1119/Gothub",
  },
  {
    name: "ClipSift: AI Video Digest",
    description: "Natural language video search engine for YouTube content. Query and extract relevant clips for vlogs and content creation using semantic search. Built with video embeddings and retrieval systems. Gained 40 stars in 2 weeks, contender for Backdrop V3 Race Hackathon.",
    githubUrl: "https://github.com/wayne-wang-1119/ClipSift-AI-Powered-Video-Digest/",
  },
  {
    name: "CktGym",
    description: "Decentralized solution for training circuits and optimizing designs using reinforcement learning. Developed hardware RL simulation system with OpenAI Gym and Ray on GCP infrastructure. Funded by NSF and Intel. Demoed at Berkeley Data Science Discovery Fair 2023.",
    githubUrl: "https://github.com/BWRC-AMS-ML-Discovery/BwrcAmsMlDiscovery",
  },
  {
    name: "Reunion",
    description: "Few-shot voice cloning system to preserve memories of loved ones. Clone human voice and chat patterns using minimal audio samples. Built for personal preservation and legacy.",
    githubUrl: "https://github.com/wayne-wang-1119/Reunion",
  },
  {
    name: "FindMoney",
    description: "Flutter mobile application connecting established businesses with angel investors. Helps small businesses discover similar companies funded by angels. Raised $20K from angel investors.",
    githubUrl: "https://github.com/wayne-wang-1119/findmoney",
  },
];

const publications = [
  {
    title: "Generative-CNN for Pattern Recognition in Finance",
    abstract: "Leveraging GANs to synthesize candlestick chart images for stocks, enabling CNNs to predict market movements through visual pattern recognition. Massively reduces training data requirements for financial vision models.",
    link: "https://dl.acm.org/doi/10.1145/3677052.3698622",
    venue: "ICAIF 2024",
  },
  {
    title: "Performant LLM Agentic Framework for Conversational AI",
    abstract: "Novel system combining LLM-based reasoning with mathematically grounded vector scoring for navigating complex graph-based workflows. Addresses alignment errors and hallucinations in voice AI systems. Achieves higher accuracy and reduced latency for real-time conversational AI in business environments.",
    link: "https://arxiv.org/abs/2503.06410",
    venue: "IEEE 2025",
  }
];

/*
 * Every section reduces to one card shape, so a theme writes a single Card
 * renderer instead of three. Themes control how these render; they never
 * change what is rendered.
 */
const experienceCards: CardItem[] = experiences.map((exp) => ({
  title: exp.title,
  href: exp.link,
  subtitle: exp.role,
  meta: exp.dates,
  description: exp.description,
}));

const projectCards: CardItem[] = projects.map((project) => ({
  title: project.name,
  description: project.description,
  action: { label: "View →", href: project.githubUrl },
}));

const publicationCards: CardItem[] = publications.map((pub) => ({
  title: pub.title,
  meta: pub.venue,
  description: pub.abstract,
  action: { label: "View →", href: pub.link },
}));

type SectionProps = {
  title: string;
  index: number;
  items: CardItem[];
  /** When set, only this many render until the visitor expands. */
  initialCount?: number;
};

const Section: React.FC<SectionProps> = ({
  title,
  index,
  items,
  initialCount,
}) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = React.useState(false);

  const collapsed = initialCount !== undefined && !expanded;
  const visible = collapsed ? items.slice(0, initialCount) : items;
  const hidden = initialCount === undefined ? 0 : items.length - initialCount;

  return (
    <section className="section">
      <theme.SectionHeader title={title} index={index} />
      <div className="section-content">
        {visible.map((item, idx) => (
          <motion.div
            key={item.title}
            className="reveal-item"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.55,
              // Capped so a long list does not end up waiting on itself.
              delay: Math.min(idx, 4) * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <theme.Card item={item} index={idx} />
          </motion.div>
        ))}

        {hidden > 0 && (
          <button
            className="expand-button"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Show less" : `Show ${hidden} more`}
          </button>
        )}
      </div>
    </section>
  );
};

const App = () => {
  const { theme, themeId } = useTheme();
  const { Hero, Backdrop, Chrome } = theme;
  const texture = TEXTURES[themeId];

  return (
    <div
      className="app"
      /* Gate weave is animated in CSS but specified in TS, so the amplitude
         and period reach the keyframes as custom properties rather than being
         restated per theme in a stylesheet and left to drift. */
      style={
        {
          "--weave": `${texture.weave}px`,
          "--weave-period": `${texture.weavePeriod}s`,
        } as React.CSSProperties
      }
    >
      {/*
        Backdrop and chrome cross-fade on a theme change rather than cutting.
        Keyed on themeId so AnimatePresence sees a new subtree; the content
        itself never remounts.

        Two details are load-bearing, and both were wrong at first:

        No `mode="wait"`. Sequencing the exit before the entrance leaves the
        page wearing the new theme's typography over the old theme's backdrop
        for the whole exit, then pops the new backdrop in. Overlapping them is
        what makes it read as a dissolve.

        `data-theme` on the wrapper. Theme tokens are element-scoped, and the
        root attribute flips the instant the theme changes — so without this,
        the outgoing layer would spend its exit repainted in the incoming
        theme's palette.
      */}
      <AnimatePresence>
        <motion.div
          key={themeId}
          data-theme={themeId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeInOut" }}
        >
          {Backdrop && <Backdrop />}
          {Chrome && <Chrome />}
        </motion.div>
      </AnimatePresence>

      {/* Covers the page so the theme swap happens unseen. */}
      <ThemeTransition />

      {/* Above the content, not behind it: grain lives in the print, so it
          falls across the type as well as the gaps. */}
      <FilmGrain themeId={themeId} />

      {/* Hero */}
      <section className="hero">
        <Hero name={NAME} tagline={TAGLINE} />

        <div className="hero-socials">
          <a
            className="social-link"
            href="https://www.linkedin.com/in/wayne-wang-380029197/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
            </svg>
          </a>
          <a
            className="social-link"
            href="https://github.com/wayne-wang-1119"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 .5C5.37.5 0 5.78 0 12.29c0 5.2 3.44 9.6 8.21 11.16.6.11.82-.25.82-.56 0-.27-.01-1.16-.02-2.1-3.34.7-4.04-1.4-4.04-1.4-.55-1.36-1.34-1.72-1.34-1.72-1.09-.72.08-.71.08-.71 1.2.08 1.84 1.21 1.84 1.21 1.07 1.78 2.81 1.27 3.5.97.11-.76.42-1.27.76-1.56-2.67-.29-5.47-1.3-5.47-5.78 0-1.28.47-2.32 1.24-3.14-.12-.29-.54-1.48.12-3.08 0 0 1.01-.31 3.3 1.2.96-.26 1.98-.39 3-.4 1.02 0 2.04.14 3 .4 2.28-1.52 3.29-1.2 3.29-1.2.66 1.6.24 2.79.12 3.08.77.82 1.24 1.86 1.24 3.14 0 4.49-2.81 5.48-5.49 5.77.43.36.81 1.08.81 2.18 0 1.58-.01 2.85-.01 3.24 0 .31.21.68.83.56C20.57 21.88 24 17.48 24 12.29 24 5.78 18.63.5 12 .5z" />
            </svg>
          </a>
          <a
            className="social-link"
            href="https://twitter.com/thehaohanwang"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X (Twitter)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        </div>

        <div
          className="scroll-indicator"
          onClick={() =>
            window.scrollTo({ top: window.innerHeight, behavior: "smooth" })
          }
        >
          ↓
        </div>
      </section>

      <Section title="Experience" index={0} items={experienceCards} initialCount={4} />
      <div className="section-divider">↓</div>

      <Section title="Projects" index={1} items={projectCards} initialCount={3} />
      <div className="section-divider">↓</div>

      <Section title="Publications" index={2} items={publicationCards} />

      <ThemeSwitcher />
    </div>
  );
};

export default App;
